import { BaseWebComponentView } from '../../../framework/BaseWebComponentView';
import { CardHistoryWebComponent } from '../../cardHistory';
import { IncidentActions } from '../types';
import { formatDate } from '@nirvana/shared/time';
import { TemplateState } from '../../../types/common/types';
import { htmlEncode } from '../../../utils/escape.utils';

export class IncidentHistoryView extends BaseWebComponentView {
  getDomElements() {
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$panelSideMenu = this.shadow.querySelector('#incidentHistoryPanelSideMenu');
    this.dom.incidentHistoryTitle = this.shadow.querySelector('#incidentHistoryTitle');
    this.dom.$incidentHistoryList = this.shadow.querySelector('#incidentHistoryList');
    this.dom.$emptyState = this.shadow.querySelector('#emptyState');
    this.dom.$imageRepoTextEmpty = this.shadow.querySelector('#imageRepoTextEmpty');
    this.dom.$imageRepoTitleEmpty = this.shadow.querySelector('#imageRepoTitleEmpty');
    this.dom.$imageRepoTextError = this.shadow.querySelector('#imageRepoTextError');
    this.dom.$imageRepoTitleError = this.shadow.querySelector('#imageRepoTitleError');
    this.dom.$imageRepoContainer = this.shadow.querySelector('#emptyState');

    this.setTranslatedStrings();
  }

  buildSuccessView(data) {
    if (data.incidentsHistory.length > 0) {
      for (const incidentHistory of data.incidentsHistory) {
        this.addIncidentHistoryCard(incidentHistory);
      }
    } else {
      this.buildEmptyState();
    }
  }

  buildErrorView(error: Error) {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  buildEmptyState() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.EMPTY);
  }

  setLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  setScrollLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.SCROLL_LOADING);
  }

  removeTemplateState() {
    this.resetTemplateState(this.dom.$wrapper);
  }

  public setTranslatedStrings() {
    this.dom.incidentHistoryTitle.textContent = this.dependencies.translateFn('Incident history');
    this.dom.$imageRepoTextEmpty.textContent = this.dependencies.translateFn(
      "You don't have any incidents history yet"
    );
    this.dom.$imageRepoTitleEmpty.textContent = this.dependencies.translateFn('No history');
    this.dom.$imageRepoTextError.textContent = this.dependencies.translateFn(
      'There was an error. Please try again later.'
    );
    this.dom.$imageRepoTitleError.textContent = this.dependencies.translateFn('Something went wrong');
  }

  addIncidentHistoryCard(incidentHistory) {
    const incidentHistoryCard = new CardHistoryWebComponent();

    const { name, description } = this.getIncidentHistoryDetailsBasedOnType(incidentHistory);

    incidentHistoryCard.name = name;
    incidentHistoryCard.innerHTML = description;
    incidentHistoryCard.date = formatDate(incidentHistory.created);

    this.dom.$incidentHistoryList.append(incidentHistoryCard);
  }

  getIncidentHistoryDetailsBasedOnType(incidentHistory) {
    switch (incidentHistory.type) {
      case IncidentActions.CREATED:
        return {
          name: this.dependencies.translateFn('Created'),
          description: this.dependencies.translateFn('The incident was created.'),
        };

      case IncidentActions.UPDATED:
        return this.getDescriptionForIncidentUpdated(incidentHistory);

      case IncidentActions.STATUS_CHANGED:
        const username = this.computeUsername(incidentHistory.specifics?.user);

        return {
          name: this.dependencies.translateFn('Status changed'),
          description: this.dependencies.translateFn('{username} set the incident status to {newStatus}.', {
            username,
            newStatus: `<span class="text-uppercase">${this.dependencies.Ext.translateEdrIncidentStatus(
              incidentHistory.specifics.newStatus
            )}</span>`,
          }),
        };

      case IncidentActions.INCIDENT_ASSIGNED:
        const reporterUsername = this.computeUsername(incidentHistory.specifics?.reporterUser);
        const assignedUsername = this.computeUsername(incidentHistory.specifics?.assignedUser);

        return {
          name: this.dependencies.translateFn('Assigned'),
          description: this.dependencies.translateFn(
            '{reporterUsername} assigned the incident to {assignedUsername}.',
            {
              reporterUsername,
              assignedUsername,
            }
          ),
        };

      case IncidentActions.INCIDENT_UNASSIGNED:
        const reporterUser = this.computeUsername(incidentHistory.specifics?.reporterUser);
        const assignedUser = this.computeUsername(incidentHistory.specifics?.assignedUser);

        return {
          name: this.dependencies.translateFn('Unassigned'),
          description: this.dependencies.translateFn('{reporterUser} unassigned the incident from {assignedUser}.', {
            reporterUser,
            assignedUser,
          }),
        };

      case IncidentActions.PRIORITY_CHANGED:
        const user = this.computeUsername(incidentHistory.specifics?.user);

        return {
          name: this.dependencies.translateFn('Priority changed'),
          description: this.dependencies.translateFn('{user} set the incident priority to {newPriority}.', {
            user,
            newPriority: `<span fontSize="12" class="text-uppercase">${this.dependencies.Ext.translateEdrIncidentPriority(
              incidentHistory.specifics.newPriority
            )}</span>`,
          }),
        };

      case IncidentActions.NOTE_ADDED:
        const userNoteAdded = this.computeUsername(incidentHistory.specifics?.user);

        return {
          name: this.dependencies.translateFn('Note added'),
          description: this.dependencies.translateFn('{userNoteAdded} added an incident note.', {
            userNoteAdded,
          }),
        };

      case IncidentActions.NODE_UPDATED:
        const userNoteUpdated = this.computeUsername(incidentHistory.specifics?.user);

        return {
          name: this.dependencies.translateFn('Note edited'),
          description: this.dependencies.translateFn('{userNoteUpdated} edited an incident note.', {
            userNoteUpdated,
          }),
        };

      case IncidentActions.NOTE_DELETED:
        const userNoteDeleted = this.computeUsername(incidentHistory.specifics?.user);

        return {
          name: this.dependencies.translateFn('Note deleted'),
          description: this.dependencies.translateFn('{userNoteDeleted} deleted an incident note.', {
            userNoteDeleted,
          }),
        };

      default:
        return {
          name: this.dependencies.translateFn('N/A'),
          description: this.dependencies.translateFn('N/A'),
        };
    }
  }

  computeUsername(user) {
    if (!user) {
      return this.dependencies.translateFn('N/A');
    }

    if (user.company && user.company._id !== this.dependencies.user.company.id) {
      return htmlEncode(`${user.company.name} (${user.name})`);
    }
    return htmlEncode(user.name);
  }

  getDescriptionForIncidentUpdated(incidentHistory) {
    const incidentHistoryDetails = {
      name: this.dependencies.translateFn('Updated'),
      description: this.dependencies.translateFn('The incident was updated.'),
    };

    if (incidentHistory.specifics?.newNodesCount > 0) {
      incidentHistoryDetails.description +=
        ' ' +
        this.dependencies.translateFn('New nodes: {newNodesCount}.', {
          newNodesCount: incidentHistory.specifics.newNodesCount,
        });
    }

    if (incidentHistory.specifics?.newAlertsCount > 0) {
      incidentHistoryDetails.description +=
        ' ' +
        this.dependencies.translateFn('New alerts: {newAlertsCount}.', {
          newAlertsCount: incidentHistory.specifics.newAlertsCount,
        });
    }

    if (incidentHistory.specifics?.oldStatus) {
      incidentHistoryDetails.description +=
        ' ' +
        this.dependencies.translateFn('Status has changed from {oldStatus} to {defaultStatus}.', {
          oldStatus: `<span class="text-uppercase">${this.dependencies.Ext.translateEdrIncidentStatus(
            incidentHistory.specifics.oldStatus
          )}</span>`,
          defaultStatus: `<span class="text-uppercase">${this.dependencies.Ext.translateEdrIncidentStatus(1)}</span>`,
        });
    }

    return incidentHistoryDetails;
  }
}
